import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import toast from 'react-hot-toast';
import { ChevronLeft, FileText, Download, Eye, Clock, AlertCircle, UploadCloud, UserCircle, DollarSign, CheckCircle, UserCheck, Send, XCircle } from 'lucide-react';
import api from '../services/api.js';
import FacturacionCardInfo from '../components/FacturacionCardInfo.jsx';
import { getComprobantesAceptados } from '../utils/formatters.js';

const ACTION_LABELS = {
  creacion:                     { label: 'Trámite Ingresado',                color: 'var(--primary)' },
  cambio_estado:                { label: 'Cambio de Estado',                 color: '#7c3aed' },
  asignacion:                   { label: 'Trámite Asignado',                 color: '#0284c7' },
  cambio_estado_y_asignacion:   { label: 'Cambio de Estado y Asignación',    color: '#6366f1' },
  envio_cotizacion:             { label: 'Cotización Emitida',               color: '#d97706' },
  confirmacion_pago:            { label: 'Pago Confirmado',                  color: '#059669' },
  rechazo_pago:                 { label: 'Pago Rechazado',                   color: '#dc2626' },
  devolucion:                   { label: 'Devolución de Fondos',             color: '#d97706' },
  adjunto_documento:            { label: 'Documento Adjuntado',              color: '#059669' },
  correo_enviado:               { label: 'Correo Enviado al Ciudadano',      color: '#d97706' },
  respuesta_ciudadano:          { label: 'Respuesta del Ciudadano',          color: '#0891b2' },
  rectificacion_tipo:           { label: 'Rectificación de Acto / Inscripción', color: '#ea580c' },
  marcado_exento_repertorio:    { label: 'Marcado Exento de Repertorio',     color: '#4f46e5' },
  desmarcado_exento_repertorio: { label: 'Desmarcado de Exención',           color: '#64748b' },
  reapertura_staff:             { label: 'Trámite Reabierto',                color: '#ea580c' },
  entrega_documentos:           { label: 'Documentos Entregados',           color: '#059669' },
  rechazo:                      { label: 'Trámite Rechazado',                color: '#dc2626' },
  actualizacion:                { label: 'Actualización de Datos',           color: '#64748b' },
};

/* ─── Sub-componente: Modal Correo Ciudadano ─────────────────────────────── */
function ModalCorreo({ tramite, replyTo, onClose, onSent }) {
  const [asunto, setAsunto]   = useState(replyTo ? `Re: ${replyTo.payload?.asunto || 'Tu mensaje'}` : '');
  const [mensaje, setMensaje] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!mensaje.trim()) {
      toast.error('El mensaje es obligatorio.');
      return;
    }
    setSending(true);
    try {
      await api.post(`/tramites/${tramite.id}/correo`, {
        asunto: asunto || 'Respuesta a tu requerimiento',
        mensaje,
        reply_to_step_id: replyTo?.id
      });
      toast.success('Correo enviado al ciudadano y registrado en el historial.');
      onSent();
      onClose();
    } catch {
      toast.error('Error al enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
      <div className="modal-content" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <Send size={18} color="var(--primary)" /> Enviar Correo al Ciudadano
          </h2>
          <button className="modal-close" onClick={onClose}><XCircle size={18} /></button>
        </div>

        <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '0.8rem 0 1rem 0' }}>
          Para: <strong>{tramite.nombre_solicitante}</strong> ({tramite.correo})
          &nbsp;·&nbsp;Trámite <strong>{tramite.numero_seguimiento}</strong>
        </p>

        {replyTo && (
          <div style={{
            background: '#f8fafc', padding: '0.8rem', borderRadius: 8, marginBottom: '1.2rem',
            borderLeft: '4px solid var(--primary)', border: '1px solid #e2e8f0', borderLeftWidth: 4
          }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--primary)', fontWeight: 800, marginBottom: 4, textTransform: 'uppercase' }}>
              Mensaje del Ciudadano:
            </div>
            <div style={{ fontSize: '0.8rem', color: '#475569', fontStyle: 'italic', lineHeight: 1.4 }}>
              "{replyTo.payload?.mensaje || replyTo.payload?.comentario}"
            </div>
          </div>
        )}

        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.82rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Asunto</label>
          <input
            className="form-control"
            value={asunto}
            onChange={e => setAsunto(e.target.value)}
            placeholder="Ej: Información sobre plazo de trámite"
            style={{ width: '100%', fontSize: '0.85rem' }}
          />
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.82rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Mensaje de Respuesta</label>
          <textarea
            className="form-control"
            rows={5}
            value={mensaje}
            onChange={e => setMensaje(e.target.value)}
            placeholder="Escribe aquí tu respuesta para el ciudadano..."
            style={{ width: '100%', fontSize: '0.85rem' }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.2rem' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {sending ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <><Send size={14} /> Enviar Respuesta</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HistorialTramite() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isFromConsole = location.state?.from === 'console';

  const [tramite, setTramite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalCorreo, setModalCorreo] = useState(null); // { item, replyTo }

  // Reapertura Administrativa (Staff)
  const [reabrirFiles, setReabrirFiles] = useState([]);
  const [loadingReabrir, setLoadingReabrir] = useState(false);
  const [estadoDestino, setEstadoDestino] = useState('Ingresado'); 
  const [estados, setEstados] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  useEffect(() => {
    fetchTramite();
  }, [id]);

  const fetchTramite = async () => {
    try {
      const [resTramite, resEstados, resUsuarios] = await Promise.all([
        api.get(`/tramites/${id}`),
        api.get('/estados'),
        api.get('/users/assignable')
      ]);
      setTramite(resTramite.data);
      const tramiteEstados = resEstados.data.filter(e => e.tipo === 'tramite');
      setEstados(tramiteEstados);
      setUsuarios(resUsuarios.data || []);
    } catch (err) {
      toast.error('Error al cargar la información del caso.');
      navigate('/tramites');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDoc = (doc) => {
    const url = `${api.defaults.baseURL}/tramites/documentos/${doc.token_descarga}/descargar`;
    window.open(url, '_blank');
  };

  const handleViewDoc = (doc) => {
    const url = `${api.defaults.baseURL}/tramites/documentos/${doc.token_descarga}`;
    window.open(url, '_blank');
  };

  const handleFileChange = (e) => {
    setReabrirFiles(Array.from(e.target.files));
  };

  const submitReabrir = async () => {
    // Si bien puede reabrirlo sin documentos si así lo acuerdan, 
    // asumimos que el funcionario adjunta lo que trajeron físico.
    const formData = new FormData();
    if (reabrirFiles.length > 0) {
        reabrirFiles.forEach(f => formData.append('documentos[]', f));
    } else {
        // Enviar un array vacío o null para que Laravel no explote si es solo revisión
        formData.append('documentos', '');
    }
    formData.append('estado_destino', estadoDestino);

    setLoadingReabrir(true);
    try {
      await api.post(`/tramites/${id}/reabrir-staff`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Trámite reabierto con éxito.');
      setReabrirFiles([]);
      fetchTramite(); // Recargar historial
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al intentar reabrir el trámite.');
    } finally {
      setLoadingReabrir(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '5rem' }}>
        <span className="spinner" style={{ width: '40px', height: '40px' }} />
        <p>Cargando información del caso...</p>
      </div>
    );
  }

  if (!tramite) return null;

  const comprobantesAceptados = getComprobantesAceptados(tramite);

  // Extraer documentos faltantes si está rehusado
  const isRechazado = tramite.estado?.nombre === 'Rehusado';
  let docsFaltantes = [];
  if (isRechazado && tramite.workflow_steps) {
    // Buscar el paso que causó el rechazo
    const stepRechazo = tramite.workflow_steps.find(w => w.action === 'cambio_estado' && w.payload?.motivo);
    docsFaltantes = stepRechazo?.payload?.documentos_faltantes || [];
  }

  const resolveEstado = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && isNaN(Number(val))) return val;
    const match = estados.find(e => String(e.id) === String(val));
    return match ? match.nombre : (typeof val === 'string' ? val : `Estado #${val}`);
  };

  const resolveUsuario = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && isNaN(Number(val))) return val;
    const match = usuarios.find(u => String(u.id) === String(val));
    return match ? match.name : (typeof val === 'string' ? val : `Usuario #${val}`);
  };

  const getEstadoNombre = (estadoId) => {
    return resolveEstado(estadoId);
  };

  return (
    <div className="container" style={{ padding: '2rem max(2rem, 5vw)', maxWidth: '1200px', margin: '0 auto' }}>
      
      <button 
        className="btn btn-secondary btn-sm" 
        onClick={() => navigate(isFromConsole ? '/funcionario' : '/tramites')} 
        style={{ marginBottom: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
      >
        <ChevronLeft size={16} /> {isFromConsole ? 'Volver a Consola' : 'Volver a Trámites'}
      </button>


      <div className="glass-card" style={{ marginBottom: '2rem', padding: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.8rem', color: 'var(--primary-dark)' }}>
              Trámite <span style={{ fontFamily: 'monospace', color: 'var(--copper)' }}>{tramite.numero_seguimiento}</span>
            </h1>
            <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-muted)' }}>
              RUT Solicita: <strong>{tramite.rut_solicitante}</strong> — {tramite.nombre_solicitante}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {comprobantesAceptados.length === 1 && (
              <a
                href={comprobantesAceptados[0].comprobante_url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm"
                style={{
                  background: '#059669',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  padding: '6px 12px',
                  textDecoration: 'none',
                  boxShadow: '0 1px 2px rgba(5, 150, 105, 0.25)'
                }}
                title="Ver Comprobante de Pago Aceptado en pestaña nueva"
              >
                <CheckCircle size={14} color="#ffffff" /> Comprobante
              </a>
            )}
            {comprobantesAceptados.length > 1 && comprobantesAceptados.map((comp, cIdx) => (
              <a
                key={comp.id || cIdx}
                href={comp.comprobante_url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm"
                style={{
                  background: '#059669',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  padding: '5px 10px',
                  textDecoration: 'none',
                  boxShadow: '0 1px 2px rgba(5, 150, 105, 0.25)'
                }}
                title={`Ver Comprobante #${cIdx + 1} (${comp.total ? '$' + Number(comp.total).toLocaleString('es-CL') : ''})`}
              >
                <CheckCircle size={13} color="#ffffff" /> Comprobante #{cIdx + 1}
              </a>
            ))}
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setModalCorreo({ item: tramite, replyTo: null })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Send size={15} /> Responder al Ciudadano
            </button>
            <span className={`badge`} style={{
              fontSize: '1rem', padding: '0.5rem 1rem',
              backgroundColor: isRechazado ? '#fef2f2' : 'transparent',
              color: isRechazado ? '#ef4444' : 'inherit',
              border: isRechazado ? '1px solid #fca5a5' : '1px solid var(--border-color)'
            }}>
              {tramite.estado?.nombre}
            </span>
          </div>
        </div>
      </div>

      {/* Ficha de Facturación / Boleta */}
      <FacturacionCardInfo data={tramite} style={{ marginBottom: '2rem' }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '2rem' }}>
        
        {/* Left Col: Timeline (WorkflowSteps) */}
        <div>
          <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={20} style={{ color: 'var(--copper)' }} /> Historial de Acciones
          </h3>

          <div style={{ position: 'relative', paddingLeft: '1.5rem', borderLeft: '2px solid rgba(var(--copper-rgb), 0.2)' }}>
            {tramite.workflow_steps?.map((step, idx) => (
              <div key={step.id} style={{ marginBottom: '1.5rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: '-29px', top: '2px', background: 'var(--bg-body)', padding: '4px', borderRadius: '50%' }}>
                  <div style={{ width: '10px', height: '10px', background: 'var(--copper)', borderRadius: '50%' }} />
                </span>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {new Date(step.created_at).toLocaleString('es-CL')} 
                  {step.action !== 'respuesta_ciudadano' && (
                    <span style={{ marginLeft: '10px' }}>
                      <UserCircle size={12} style={{ display: 'inline', marginRight: 4 }} />
                      {(() => {
                        if (step.display_worker_name) return step.display_worker_name;
                        if (step.payload?.usuario || step.payload?.funcionario) {
                          const u = String(step.payload.usuario || step.payload.funcionario).trim();
                          if (u.toLowerCase().includes('admin')) return 'Sistema';
                          return u;
                        }
                        if (!step.worker) return 'Sistema';
                        const name = (step.worker.name || '').trim();
                        const role = (step.worker.role || '').toLowerCase();
                        if (!name || name.toLowerCase().includes('admin') || role === 'admin' || role === 'super_admin' || step.worker.id === 1) {
                          return 'Sistema';
                        }
                        return name;
                      })()}
                    </span>
                  )}
                </p>
                <div style={{ 
                  background: 'var(--bg-surface)', 
                  padding: '1rem', 
                  borderRadius: '6px', 
                  border: '1px solid rgba(255,255,255,0.05)',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
                }}>
                  <strong style={{ display: 'block', marginBottom: '8px', color: ACTION_LABELS[step.action]?.color || 'var(--primary)' }}>
                    {ACTION_LABELS[step.action]?.label || step.action.replace(/_/g, ' ')}
                  </strong>
                  
                  {step.payload?.asunto && (
                    <div style={{ margin: '0 0 6px 0', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-color)' }}>
                      <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>Asunto:</span> {step.payload.asunto}
                    </div>
                  )}

                  {(step.payload?.mensaje || step.payload?.comentario) && (
                    <div style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '0.85rem', 
                      color: 'var(--text-muted)', 
                      whiteSpace: 'pre-wrap', 
                      background: 'rgba(0, 0, 0, 0.02)', 
                      padding: '8px 12px', 
                      borderRadius: '6px', 
                      border: '1px solid var(--border-color)',
                      lineHeight: 1.5 
                    }}>
                      {step.payload.mensaje || step.payload.comentario}
                    </div>
                  )}

                  {step.payload?.nota && !(step.payload?.mensaje || step.payload?.comentario) && (
                     <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', fontStyle: 'italic' }}>{step.payload.nota}</p>
                  )}

                  {/* Detalle de Asignación */}
                  {(step.action === 'asignacion' || step.action === 'cambio_estado_y_asignacion' || step.payload?.changes?.asignado_a) && (
                    <div style={{ fontSize: '0.85rem', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <UserCheck size={14} style={{ color: '#0284c7' }} />
                      <span>
                        <strong>Asignado a:</strong>{' '}
                        <strong style={{ color: '#0369a1' }}>
                          {resolveUsuario(step.payload?.changes?.asignado_a?.new || step.payload?.funcionario || 'Funcionario')}
                        </strong>
                        {step.payload?.changes?.asignado_a?.old && step.payload?.changes?.asignado_a?.old !== 'Sin asignar' && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginLeft: 6 }}>
                            (anteriormente: {resolveUsuario(step.payload.changes.asignado_a.old)})
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Detalle de Cambio de Estado */}
                  {(step.action === 'cambio_estado' || step.action === 'cambio_estado_y_asignacion' || step.payload?.changes?.estado) && (
                    <div style={{ fontSize: '0.85rem', margin: '0 0 8px 0' }}>
                      {step.payload?.changes?.estado ? (
                        <>
                          Estado anterior <strong>{resolveEstado(step.payload.changes.estado.old)}</strong> ➔ Estado nuevo <strong>{resolveEstado(step.payload.changes.estado.new)}</strong>
                        </>
                      ) : (
                        <>
                          Estado anterior <strong>{resolveEstado(step.old_state)}</strong> ➔ Estado nuevo <strong>{resolveEstado(step.new_state)}</strong>
                        </>
                      )}
                    </div>
                  )}

                  {/* Detalle de Cotización */}
                  {step.action === 'envio_cotizacion' && (
                    <div style={{ fontSize: '0.85rem', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 6, color: '#b45309' }}>
                      <DollarSign size={14} />
                      <span>
                        <strong>Total Cotizado:</strong> ${Number(step.payload?.total || 0).toLocaleString('es-CL')}
                        {step.payload?.caja && (
                          <span style={{ marginLeft: 6, color: 'var(--text-muted)' }}>
                            · Caja: {step.payload.caja}
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Detalle de Confirmación de Pago */}
                  {step.action === 'confirmacion_pago' && (
                    <div style={{ fontSize: '0.85rem', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 6, color: '#15803d' }}>
                      <CheckCircle size={14} />
                      <span>
                        <strong>Monto confirmado:</strong> ${Number(step.payload?.total || step.payload?.monto || 0).toLocaleString('es-CL')}
                      </span>
                    </div>
                  )}

                  {/* Otros campos modificados */}
                  {step.payload?.changes && Object.keys(step.payload.changes).filter(k => !['estado', 'estado_id', 'asignado_a'].includes(k)).length > 0 && (
                    <div style={{ margin: '0 0 8px 0', display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
                      {Object.entries(step.payload.changes)
                        .filter(([k]) => !['estado', 'estado_id', 'asignado_a'].includes(k))
                        .map(([field, val]) => (
                          <div key={field} style={{ background: 'rgba(0,0,0,0.02)', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-color)' }}>
                            <strong style={{ textTransform: 'capitalize' }}>{field.replace(/_/g, ' ')}:</strong>{' '}
                            <span style={{ color: 'var(--text-muted)', textDecoration: 'line-through' }}>{String(val?.old ?? '—')}</span>
                            <span style={{ margin: '0 4px', color: 'var(--text-muted)' }}>➔</span>
                            <strong style={{ color: 'var(--primary)' }}>{String(val?.new ?? '—')}</strong>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Comentarios de auditoría */}
                  {step.comments && !step.payload?.mensaje && !step.payload?.comentario && step.action !== 'rectificacion_tipo' && (
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 8px 0', fontStyle: 'italic' }}>
                      {step.comments}
                    </div>
                  )}

                  {step.payload?.motivo && (
                    <div style={{ marginTop: '10px', background: '#fef2f2', padding: '10px', borderRadius: '4px', borderLeft: '3px solid #ef4444' }}>
                      <strong style={{ color: '#b91c1c', fontSize: '0.85rem' }}>Motivo de Rechazo / Observación:</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: '#7f1d1d' }}>{step.payload.motivo}</p>
                      
                      {step.payload.documentos_faltantes && step.payload.documentos_faltantes.length > 0 && (
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(239, 68, 68, 0.2)' }}>
                          <strong style={{ color: '#b91c1c', fontSize: '0.8rem' }}>Documentos Exigidos para Subsanar:</strong>
                          <ul style={{ margin: '4px 0 0 0', paddingLeft: '1.2rem', fontSize: '0.85rem', color: '#7f1d1d' }}>
                            {step.payload.documentos_faltantes.map((doc, idx) => (
                               <li key={idx}>{doc}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Botón de Respuesta al Ciudadano si es un mensaje de este */}
                  {step.action === 'respuesta_ciudadano' && (
                    <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => setModalCorreo({ item: tramite, replyTo: step })}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', padding: '5px 12px', borderRadius: '6px' }}
                      >
                        <Send size={13} /> Responder al Ciudadano
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {tramite.workflow_steps?.length === 0 && (
              <p className="text-muted">No hay historial registrado.</p>
            )}
          </div>
        </div>

        {/* Right Col: Documents and Reabrir Form */}
        <div>
          <div className="glass-card" style={{ marginBottom: '2rem' }}>
            <h4 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText size={18} /> Documentos Adjuntos
            </h4>

            {/* Comprobantes de Pago Aceptados */}
            {comprobantesAceptados.map((cot, idx) => (
              <div
                key={cot.id || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.8rem',
                  background: '#ecfdf5',
                  borderRadius: '6px',
                  marginBottom: '0.8rem',
                  border: '1px solid #a7f3d0'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', fontWeight: 600, color: '#065f46' }}>
                  <CheckCircle size={16} color="#059669" />
                  <span>
                    {comprobantesAceptados.length > 1 ? `Comprobante de Pago #${idx + 1}` : 'Comprobante de Pago'}
                    {cot.total ? ` ($${Number(cot.total).toLocaleString('es-CL')})` : ''}
                  </span>
                  <span style={{ fontSize: '0.72rem', padding: '2px 6px', background: '#d1fae5', color: '#047857', borderRadius: '4px', fontWeight: 700 }}>
                    {(tramite.estado?.nombre || '').toLowerCase().includes('recibid') && cot.estado !== 'pagada' ? 'Recibido' : 'Aceptado'}
                  </span>
                </div>
                <a
                  href={cot.comprobante_url}
                  target="_blank"
                  rel="noreferrer"
                  className="icon-btn"
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.8rem',
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    textDecoration: 'none',
                    background: 'white',
                    border: '1px solid #a7f3d0',
                    borderRadius: '4px',
                    fontWeight: 700
                  }}
                  title="Ver comprobante en pestaña"
                >
                  <Eye size={14} /> Ver
                </a>
              </div>
            ))}
            
            {tramite.documentos?.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {tramite.documentos.map(doc => (
                  <li key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.8rem', background: 'var(--bg-body)', borderRadius: '6px', marginBottom: '0.5rem', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '0.85rem', wordBreak: 'break-all', paddingRight: '1rem' }}>
                      <FileText size={14} style={{ display: 'inline', marginRight: '6px', color: 'var(--copper)' }} />
                      {doc.nombre_original}
                      {doc.repertorio && (
                        <span style={{ marginLeft: '10px', fontSize: '0.75rem', padding: '2px 6px', background: 'var(--primary-light)', color: 'var(--primary-dark)', borderRadius: '4px', fontWeight: 600 }}>
                          Repertorio #{doc.repertorio.numero_repertorio}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button className="icon-btn" onClick={() => handleViewDoc(doc)} style={{ padding: '6px 10px', fontSize: '0.8rem' }} title="Ver documento en pestaña">
                        <Eye size={14} />
                      </button>
                      <button className="icon-btn" onClick={() => handleDownloadDoc(doc)} style={{ padding: '6px 10px', fontSize: '0.8rem' }} title="Descargar archivo">
                        <Download size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>No hay documentos en este caso.</p>
            )}
          </div>

          {/* Repertorios Asignados */}
          {tramite.repertorios && tramite.repertorios.length > 0 && (
            <div className="glass-card animate-fade-in" style={{ marginBottom: '2rem' }}>
              <h4 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.2em' }}>📚</span> Repertorios Registrados
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {tramite.repertorios.map(rep => (
                      <li key={rep.id} style={{ padding: '0.8rem', background: 'var(--bg-body)', borderRadius: '6px', marginBottom: '0.5rem', border: '1px solid var(--border-color)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                             <strong style={{ color: 'var(--copper)', fontSize: '0.9rem' }}>#{rep.numero_repertorio}</strong>
                             <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>{rep.estado?.nombre}</span>
                          </div>
                          <div style={{ fontSize: '0.8rem', marginTop: '4px', fontWeight: 600 }}>
                             {rep.contrato?.nombre === 'Otro' ? rep.contrato_otro : rep.contrato?.nombre} <span style={{ fontWeight: 'normal', color: 'var(--gray-500)' }}>— Requirente: {rep.nombre_requirente}</span>
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                             Fecha y Hora: {new Date(rep.fecha_repertorio).toLocaleString('es-CL', { hour12: false, dateStyle: 'short', timeStyle: 'short' })}
                          </div>
                      </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Módulo General de Gestión de Estado para Staff */}
          <div className="glass-card animate-fade-in" style={{ marginBottom: '2rem', borderLeft: '4px solid var(--primary)', background: 'var(--bg-body)' }}>
              <h4 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={18} style={{ color: 'var(--primary)' }}/> Gestión del Trámite
              </h4>
              
              <div style={{ marginBottom: '15px' }}>
                 <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '4px', fontWeight: 600 }}>Cambiar Estado Actual:</label>
                 <div style={{ display: 'flex', gap: '10px' }}>
                   <select 
                     className="form-control" 
                     value={tramite.estado_id} 
                     onChange={async (e) => {
                        try {
                           await api.patch(`/tramites/${tramite.id}/estado`, { estado_id: e.target.value });
                           toast.success('Estado actualizado correctamente');
                           fetchTramite();
                        } catch (err) {
                           toast.error('Error al cambiar el estado');
                        }
                     }}
                     style={{ fontSize: '0.85rem', padding: '6px' }}
                   >
                     {estados.map(est => (
                         <option key={est.id} value={est.id}>{est.nombre}</option>
                     ))}
                   </select>
                 </div>
                 {tramite.estado?.nombre === 'Completado' && (
                     <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem', color: 'var(--success)' }}>
                       El trámite está finalizado. Al cambiar a este estado, los repertorios también pasaron a "Inscrito".
                     </p>
                 )}
              </div>
          </div>

          {/* Módulo de Subsanación Presencial (solo si está rehusado) */}
          {isRechazado && (
            <div className="glass-card animate-fade-in" style={{ borderLeft: '4px solid #ef4444', background: '#fef2f2' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={18} /> Trámite Rehusado
              </h4>
              <div style={{ margin: '0 0 1rem 0', fontSize: '0.88rem', color: '#7f1d1d' }}>
                <strong>Motivo de Rehúso:</strong> {tramite.motivo_rehusado || 'No se indicó motivo textual.'}
              </div>

              {tramite.documento_rehusado_url && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <a
                    href={tramite.documento_rehusado_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm"
                    style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', textDecoration: 'none' }}
                  >
                    <FileText size={14} /> Ver Documento Oficial de Rehúso Adjunto
                  </a>
                </div>
              )}

              <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#991b1b', borderTop: '1px solid #fecaca', paddingTop: '0.75rem' }}>
                Reapertura administrativa con validación directa de documentación física/digital.
              </p>

              {docsFaltantes.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong style={{ fontSize: '0.85rem', color: '#b45309' }}>Se solicitan los siguientes documentos:</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: '0.85rem', color: '#b45309' }}>
                    {docsFaltantes.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ background: 'white', padding: '1rem', borderRadius: '6px', border: '1px dashed #d97706' }}>
                  <h5 style={{ margin: '0 0 0.5rem 0', color: '#92400e' }}>Subsanar Caso</h5>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Adjunte los archivos traídos por el ciudadano.</p>
                  
                  <input type="file" multiple onChange={handleFileChange} style={{ margin: '10px 0', fontSize: '0.85rem', width: '100%' }} />
                  
                  {reabrirFiles.length > 0 && (
                    <ul style={{ paddingLeft: '1.5rem', fontSize: '0.8rem', marginBottom: '10px' }}>
                      {reabrirFiles.map((f, i) => <li key={i}>{f.name}</li>)}
                    </ul>
                  )}

                  <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                     <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '4px', color: '#92400e', fontWeight: 600 }}>Pasar directamente al estado:</label>
                     <select 
                       className="form-control" 
                       value={estadoDestino} 
                       onChange={(e) => setEstadoDestino(e.target.value)}
                       style={{ fontSize: '0.85rem', padding: '6px' }}
                     >
                       <option value="Ingresado">Ingresado (Revisión Inicial)</option>
                       <option value="Procesando">Procesando (Desarrollo)</option>
                       <option value="Completado">Terminado / Completado</option>
                     </select>
                  </div>

                  <button 
                    className="btn btn-primary btn-sm" 
                    onClick={submitReabrir} 
                    disabled={loadingReabrir}
                    style={{ width: '100%', marginTop: '0.5rem', display: 'flex', justifyContent: 'center', gap: '8px' }}
                  >
                     <UploadCloud size={16} /> 
                     {loadingReabrir ? 'Enviando...' : 'Reabrir Caso Presencialmente'}
                  </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {modalCorreo && (
        <ModalCorreo
          tramite={modalCorreo.item}
          replyTo={modalCorreo.replyTo}
          onClose={() => setModalCorreo(null)}
          onSent={fetchTramite}
        />
      )}
    </div>
  );
}
